import {useMemo, useCallback} from "react";
import {UseFieldArrayAppend, UseFieldArrayRemove} from "react-hook-form";
import {Currency} from "../../../../../calls/useCases/createService/types";
import {usePricingCurrencies} from "@/features/currencies";
import {ProductVariantsFormValues} from "../../variants/types";

const POPULAR_CURRENCIES = ["NGN", "USD", "EUR", "GBP", "CAD", "AUD", "KES"];

type AppendCurrency = UseFieldArrayAppend<
  ProductVariantsFormValues,
  `variants.${number}.options.${number}.currencies`
>;

const normalize = (c: string) => c.toLowerCase();

export function useVariantCurrencyOptions(
  currencyArray: ProductVariantsFormValues["variants"][number]["options"][number]["currencies"],
  defaultCurrency: string | null,
  appendCurrency: AppendCurrency,
  removeCurrencyField: UseFieldArrayRemove,
  productCurrencies: Currency[],
  opts?: {defaultPrice?: number} // optional, avoids magic 1
) {
  const {formCurrencies} = usePricingCurrencies();
  const defaultPrice = opts?.defaultPrice ?? 100;

  // Compute a Set of already-picked codes (normalized)
  // const pickedSet = useMemo(() => {
  //   console.log("I ran picked set", currencyArray);
  //   const picked = currencyArray.map((c) => normalize(c.currency));
  //   if (defaultCurrency) {
  //     picked.push(normalize(defaultCurrency));
  //   }
  //   return new Set(picked);
  // }, [currencyArray, defaultCurrency]);

  // 1) Build a content key that changes when contents change (order-insensitive)
  const pickedKey = useMemo(() => {
    const items = [
      ...currencyArray.map((c) => normalize(c.currency)),
      ...(defaultCurrency ? [normalize(defaultCurrency)] : []),
    ];
    items.sort(); // order-insensitive
    return items.join("|"); // stable string
  }, [currencyArray, defaultCurrency]);

  // 2) Derive a stable Set from the key
  const pickedSet = useMemo(() => {
    return new Set(pickedKey ? pickedKey.split("|") : []);
  }, [pickedKey]);

  // Derive available options on every render (no local state needed)
  const availableOptions = useMemo(() => {
    const pickedSet = new Set([
      ...currencyArray.map((c) => normalize(c.currency)),
      ...(defaultCurrency ? [normalize(defaultCurrency)] : []),
    ]);
    const baseOptions =
      productCurrencies.length > 0
        ? productCurrencies
            .filter((code) => !pickedSet.has(normalize(code.callPriceCurrency)))
            .map((c) => c.callPriceCurrency.toUpperCase())
        : formCurrencies
            .filter((c) => !pickedSet.has(normalize(c)))
            .map((c) => c.toUpperCase());

    const sorted = [...baseOptions].sort((a, b) => {
      const aIndex = POPULAR_CURRENCIES.indexOf(a);
      const bIndex = POPULAR_CURRENCIES.indexOf(b);

      // NGN and other populars go first, in defined order
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Fallback: alphabetical for non-popular
      return a.localeCompare(b);
    });

    return sorted;
  }, [currencyArray, defaultCurrency, productCurrencies, formCurrencies]);

  // console.log({
  //   formCurrencies,
  //   currencyArray,
  //   defaultCurrency,
  //   // pickedSet,
  //   productCurrencies,
  //   availableOptions,
  //   // pickedSetHasFormCurrencies: formCurrencies.map(
  //   //   (code) => !pickedSet.has(normalize(code))
  //   // ),
  //   // // pickedSetHas: productCurrencies.map(
  //   // //   (code) => !pickedSet.has(normalize(code.callPriceCurrency))
  //   // // ),
  //   // // filter: productCurrencies.filter(
  //   // //   (code) => !pickedSet.has(normalize(code.callPriceCurrency))
  //   // // ),
  //   // formCurrenciesFilter: formCurrencies.filter(
  //   //   (code) => !pickedSet.has(normalize(code))
  //   // ),
  //   // // map: productCurrencies
  //   // //   .filter((code) => !pickedSet.has(normalize(code.callPriceCurrency)))
  //   // //   .map((c) => c.callPriceCurrency.toUpperCase()),
  //   // formCurrenciesMap: formCurrencies
  //   //   .filter((code) => !pickedSet.has(normalize(code)))
  //   //   .map((c) => c.toUpperCase()),
  // });

  const canAddCurrency = availableOptions.length > 0;
  const nextSuggestedCurrency = canAddCurrency ? availableOptions[0] : undefined;

  const addNewCurrency = useCallback(() => {
    if (!canAddCurrency || !nextSuggestedCurrency) return;
    appendCurrency({
      currency: nextSuggestedCurrency,
      price: defaultPrice,
    });
    // No setState needed; RHF change will recompute `availableOptions` next render.
  }, [appendCurrency, canAddCurrency, nextSuggestedCurrency, defaultPrice]);

  const removeCurrency = useCallback(
    (index: number) => {
      removeCurrencyField(index);
      // Recalculation happens automatically.
    },
    [removeCurrencyField]
  );

  const onCurrencyChange = useCallback(
    (value: string, setFieldValue: (val: string) => void) => {
      setFieldValue(value);
      // No manual recompute; the selected value lives in `currencyArray`,
      // which triggers recomputation next render.
    },
    []
  );

  return {
    currencyOptions: availableOptions, // normalized lowercase; format upstream if needed
    canAddCurrency,
    nextSuggestedCurrency,
    addNewCurrency,
    removeCurrency,
    onCurrencyChange,
  };
}
